import { useMemo, type CSSProperties } from 'react';
import { useReducedMotion } from 'framer-motion';
import { useViewport } from '@/hooks/dom/use-viewport';
import { useViewportMetrics } from '@/hooks/dom/use-viewport-metrics';
import { Viewport } from '@/theme/breakpoints';
import { Temper } from '@/types/frontend-types';
import { MESSAGE_MODAL_VARIANT, type MessageModalVariant } from './messageModalConstants';
import {
  getMessageModalContentAreaHeight,
  getMessageModalContainerStyle,
  getMessageModalContentAreaStyle,
  getMessageModalFooterButtonHeight,
  getMessageModalFooterButtonPadding,
  getMessageModalFooterButtonStyle,
  getMessageModalFooterStyle,
  getMessageModalRootStyle,
  getMessageModalSurfaceHeight,
  getMessageModalSurfaceJustifyContent,
  getMessageModalSurfacePadding,
  getMessageModalSurfaceStyle,
  MESSAGE_MODAL_SURFACE_SHADOW,
} from './messageModalStyles';

interface UseMessageModalLayoutModelInput {
  temper: Temper;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  variant: MessageModalVariant;
}

interface MessageModalLayoutModel {
  loaded: boolean;
  reduceMotion: boolean;
  vh: number;
  vw: number;
  isMobile: boolean;
  isGlobalLayout: boolean;
  footerButtonStyle: CSSProperties;
  messageModalSurfaceBoxShadow: string;
  messageModalSurfaceStyle: CSSProperties;
  messageModalContentAreaStyle?: CSSProperties;
  messageModalContentAreaHeight?: string;
  messageModalRootStyle: CSSProperties;
  messageModalContainerStyle: CSSProperties;
  messageModalFooterStyle: CSSProperties;
  messageModalFooterButtonPadding?: string;
  messageModalFooterButtonHeight?: number;
  messageModalSurfaceHeight?: string;
  messageModalSurfacePadding?: string;
  messageModalSurfaceJustifyContent?: 'center';
}

export function useMessageModalLayoutModel({
  temper,
  primaryButtonText,
  secondaryButtonText,
  variant,
}: UseMessageModalLayoutModelInput): MessageModalLayoutModel {
  const { viewport, loaded } = useViewport();
  const { vh, vw } = useViewportMetrics();
  const reduceMotion = useReducedMotion() ?? false;
  const isMobile = viewport === Viewport.Mobile;
  const isGlobalLayout = variant === MESSAGE_MODAL_VARIANT.Global;
  const hasBothFooterButtons = Boolean(primaryButtonText && secondaryButtonText);

  const footerButtonStyle = useMemo(() => getMessageModalFooterButtonStyle({
    isGlobalLayout,
    isMobile,
    hasBothFooterButtons,
  }), [hasBothFooterButtons, isGlobalLayout, isMobile]);
  const messageModalSurfaceBoxShadow = MESSAGE_MODAL_SURFACE_SHADOW;
  const messageModalSurfaceStyle = useMemo(() => getMessageModalSurfaceStyle(), []);
  const messageModalContentAreaStyle = useMemo(() => getMessageModalContentAreaStyle({
    isMobile,
  }), [isMobile]);
  const messageModalContentAreaHeight = useMemo(() => getMessageModalContentAreaHeight({
    isMobile,
  }), [isMobile]);
  const messageModalRootStyle = useMemo(() => getMessageModalRootStyle({
    isMobile,
    temper,
    vh,
    vw,
  }), [isMobile, temper, vh, vw]);
  const messageModalContainerStyle = useMemo(() => getMessageModalContainerStyle({
    isMobile,
    vh,
  }), [isMobile, vh]);
  const messageModalFooterStyle = useMemo(() => getMessageModalFooterStyle({
    isGlobalLayout,
    isMobile,
  }), [isGlobalLayout, isMobile]);
  const messageModalFooterButtonPadding = useMemo(() => getMessageModalFooterButtonPadding({
    isMobile,
  }), [isMobile]);
  const messageModalFooterButtonHeight = useMemo(() => getMessageModalFooterButtonHeight({
    isMobile,
  }), [isMobile]);
  const messageModalSurfaceHeight = useMemo(() => getMessageModalSurfaceHeight({
    isMobile,
  }), [isMobile]);
  const messageModalSurfacePadding = useMemo(() => getMessageModalSurfacePadding({
    isMobile,
  }), [isMobile]);
  const messageModalSurfaceJustifyContent = useMemo(() => getMessageModalSurfaceJustifyContent({
    isMobile,
  }), [isMobile]);

  return {
    loaded,
    reduceMotion,
    vh,
    vw,
    isMobile,
    isGlobalLayout,
    footerButtonStyle,
    messageModalSurfaceBoxShadow,
    messageModalSurfaceStyle,
    messageModalContentAreaStyle,
    messageModalContentAreaHeight,
    messageModalRootStyle,
    messageModalContainerStyle,
    messageModalFooterStyle,
    messageModalFooterButtonPadding,
    messageModalFooterButtonHeight,
    messageModalSurfaceHeight,
    messageModalSurfacePadding,
    messageModalSurfaceJustifyContent,
  };
}
